'use client';

import { useRef, type PropsWithChildren } from 'react';

import { createContext } from '@backyard-ui/utils';
import { createStore, type StoreApi } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { type Response as GetPaymentMethodsResponse } from '@/infra/services/catalog/payment/get-payment-methods';
import { type Response as GetReviewsResponse } from '@/infra/services/catalog/review/get-reviews';
import { type Response as GetReviewsSummaryResponse } from '@/infra/services/catalog/review/get-summary';

export interface DefaultState {
  review: {
    summary: GetReviewsSummaryResponse['data'] | undefined;
    reviews: GetReviewsResponse['data'] | undefined;
  };
  paymentMethods: GetPaymentMethodsResponse['data'];
}

interface DefaultActions {}

export type DefaultStore = DefaultState & DefaultActions;

interface DefaultStoreProviderProps extends PropsWithChildren {
  initialState: DefaultState;
}

const { Provider, useContext } = createContext<StoreApi<DefaultStore>>({
  name: '@store/catalog/default',
});

export function useDefaultStore<T>(selector: (store: DefaultStore) => T) {
  const api = useContext();

  return useStoreWithEqualityFn(api, selector, shallow);
}

export function DefaultStoreProvider(
  props: Readonly<DefaultStoreProviderProps>
) {
  const { children, initialState } = props;

  const storeRef = useRef(
    createStore<DefaultStore>()((set) => ({
      ...initialState,
    }))
  );

  return <Provider value={storeRef.current}>{children}</Provider>;
}
